import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { useCompanyInfo } from "@/hooks/use-company-info";

const navigation = {
  properties: [
    { name: "Short-Term Rentals", href: "/properties?type=short-term" },
    { name: "Commercial Properties", href: "/properties?type=commercial" },
    { name: "Long-Term Rentals", href: "/properties?type=long-term" },
  ],
  company: [
    { name: "About Us", href: "/about" },
    { name: "Testimonials", href: "/testimonials" },
    { name: "Contact", href: "/contact" },
  ],
};

export function Footer() {
  const { company_name, phone, email, address, facebook_url, instagram_url } = useCompanyInfo();
  
  return (
    <footer className="bg-foreground text-background">
      <div className="container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground font-display font-bold text-lg">
                {company_name.charAt(0)}
              </div>
              <p className="font-display text-xl font-semibold">{company_name}</p>
            </div>
            <p className="text-background/70 text-sm leading-relaxed mb-6">
              Your trusted partner for premium property management and real estate services.
            </p>
            <div className="flex gap-4">
              {facebook_url && (
                <a href={facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Visit our Facebook page" className="text-background/70 hover:text-primary transition-colors">
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {instagram_url && (
                <a href={instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Visit our Instagram page" className="text-background/70 hover:text-primary transition-colors">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>

          {/* Properties */}
          <div>
            <h3 className="font-display text-lg font-semibold mb-4">Properties</h3>
            <ul className="space-y-3">
              {navigation.properties.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-background/70 hover:text-primary text-sm transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-display text-lg font-semibold mb-4">Company</h3>
            <ul className="space-y-3">
              {navigation.company.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-background/70 hover:text-primary text-sm transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-display text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${phone.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-3 text-background/70 hover:text-primary text-sm transition-colors">
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  <span>{phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${email}`} className="flex items-center gap-3 text-background/70 hover:text-primary text-sm transition-colors">
                  <Mail className="h-4 w-4 flex-shrink-0" />
                  <span>{email}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-background/70 text-sm">
                  <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{address}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-background/20 mt-12 pt-8 text-center">
          <p className="text-background/60 text-sm">
            © {new Date().getFullYear()} {company_name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
